import React, { useRef, useImperativeHandle, forwardRef } from "react";
import FourColumnRowSection from "components/common/FourColumnRowSection/FourColumnRowSection";
import { StaffState } from "types/staff-edit.types";
import { Field, FieldRef } from "../Field/Field";
import { CustomerRules, onlyNumberRule } from "utils/formRules";
import { ImagePayload } from "types/image.type"; 
import UploadImageButton from "components/common/UploadImageButton/UploadButton";

interface IdentityTaxSectionProps {
  data: StaffState["identityTax"];
  onUpdate: (path: string, value: string | ImagePayload[]) => void; 
  handleUpload?: (file: File, path: string) => Promise<void>;
  isUploading?: boolean;
}

// Định nghĩa Interface cho Ref
export interface IdentityTaxSectionRef {
  validateAll: () => Promise<boolean>;
}

export const IdentityTaxSection = forwardRef<
  IdentityTaxSectionRef,
  IdentityTaxSectionProps
>(({ data, onUpdate }, ref) => {
  // Tạo Refs cho các trường cần validate
  const identityNumberRef = useRef<FieldRef>(null);
  const taxCodeRef = useRef<FieldRef>(null);
  const issuedDateRef = useRef<FieldRef>(null);
  const expiredDateRef = useRef<FieldRef>(null);
  const issuedPlaceRef = useRef<FieldRef>(null);

  // Công khai hàm validate cho cha
  useImperativeHandle(ref, () => ({
    validateAll: async () => {
      const results = await Promise.all([
        identityNumberRef.current?.validate(),
        taxCodeRef.current?.validate(),
        issuedDateRef.current?.validate(),
        expiredDateRef.current?.validate(),
        issuedPlaceRef.current?.validate(),
      ]);
      return results.every((r) => r === true);
    },
  }));

  return (
    <FourColumnRowSection
      leftTitle="Định danh & Thuế TNCN"
      rightTitle=""
      rows={[
        {
          leftLabel: "Số CMND/CCCD",
          leftValue: (
            <Field
              ref={identityNumberRef}
              path="identityTax.identityNumber"
              value={data.identityNumber}
              onUpdate={onUpdate}
              placeholder="Nhập số CMND/CCCD"
              rules={[...CustomerRules, onlyNumberRule()]}
            />
          ),
          rightLabel: "Mã số thuế cá nhân",
          rightValue: (
            <Field
              ref={taxCodeRef}
              path="identityTax.taxCode"
              value={data.taxCode}
              onUpdate={onUpdate}
              placeholder="Nhập mã số thuế"
              rules={[...CustomerRules, onlyNumberRule()]}
            />
          ),
        },
        {
          leftLabel: "Ngày cấp",
          leftValue: (
            <Field
              ref={issuedDateRef}
              type="date"
              path="identityTax.issuedDate"
              value={data.issuedDate}
              onUpdate={onUpdate}
              placeholder="Chọn ngày cấp"
              rules={CustomerRules}
            />
          ),
          rightLabel: "Ngày hết hạn",
          rightValue: (
            <Field
              ref={expiredDateRef}
              type="date"
              path="identityTax.expiredDate"
              value={data.expiredDate}
              onUpdate={onUpdate}
              placeholder="Chọn ngày hết hạn"
              rules={CustomerRules}
            />
          ),
        },
        {
          leftLabel: "Nơi cấp",
          leftValue: (
            <Field
              ref={issuedPlaceRef}
              path="identityTax.issuedPlace"
              value={data.issuedPlace}
              onUpdate={onUpdate}
              placeholder="Nhập nơi cấp"
              rules={CustomerRules}
            />
          ),
        },
        {
          leftLabel: "Ảnh",
          leftValue: (
            <div className="w-full">
              <UploadImageButton
                label="Tải lên CMND/CCCD"
                isDisabled={false}
                imagesData={data.identityImages || []}
                setImagesData={(action) => {
                  // 👈 Ép kiểu rõ ràng thành ImagePayload[] thay vì any
                  const currentImages: ImagePayload[] = data.identityImages || [];
                  
                  const updatedImages: ImagePayload[] = typeof action === 'function' 
                    ? action(currentImages) 
                    : action;

                  onUpdate("identityTax.identityImages", updatedImages);
                }}
              />
            </div>
          ),
        },
      ]} 
    />
  );
});

IdentityTaxSection.displayName = "IdentityTaxSection";